import { APIService } from '@apparel/core/api';
import { getPlatformTag } from '@apparel/core/platform';
import { cx } from '@apparel/react/cx';
import { CountriesDropdown } from '@apparel/ui/components/CountriesDropdown';
import { CountryStateDropdown } from '@apparel/ui/components/CountryStateDropdown';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { ErrorMessage, useFormikContext } from 'formik';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { PhoneField } from '../../../../fields/PhoneField';
import { AuthViewType } from '../../AuthButtonProps';
import { useNewAuthForm } from '../NewAuthFormContext';
import { useNewAuthDialogFormStyles } from './atoms';
import { FormHeader } from './FormHeader';

export const TERMS_LINK = 'https://wooterapparel.com/return-policy';

interface SignUpProps extends React.HTMLAttributes<HTMLDivElement> {
    targetSignInView?: AuthViewType;
}

export const sources = [
    'Email',
    'Instagram',
    'Facebook',
    'Twitter',
    'Recommended by friend or colleague',
    'Sales Rep',
    'NSID',
    'Other',
];

export const whatRoles = [
    'Coach',
    'Athletic Director',
    'League Admin',
    'Parent',
    'Player',
    'Other',
];

export const SignUp = forwardRef(({ className, targetSignInView = 'signIn', ...rest }: SignUpProps, ref: any) => {
    const classes = useNewAuthDialogFormStyles();
    const dialog$ = useNewAuthForm();
    const [sports, setSports] = useState([
        { id: 11, name: 'Multi-Sport' },
        { id: 22, name: 'Other' },
    ]);
    const [showPassword, setShowPassword] = useState(false);

    const { values, touched, errors, handleChange, handleBlur, setFieldValue } = useFormikContext<{
        identifier: string;
        first_name: string;
        last_name: string;
        email: string;
        phone: string;
        password: string;
        country: any;
        city_name: string;
        sport: string;
        sport_other: string;
        source: string;
        source_other: string;
        what_role: string;
        what_role_other: string;
        authorized_to_order: boolean | '';
        instagram_handle: string;
    }>();

    const handleView = useCallback((view: AuthViewType) => () => dialog$.setView(view), [dialog$.setView]);
    const handleToggleShowPassword = () => setShowPassword(!showPassword);

    useEffect(() => {
        if (values.identifier) {
            if (values.identifier.includes('.') || values.identifier.includes('@')) {
                setFieldValue('email', values.identifier);
            } else {
                setFieldValue('phone', values.identifier);
            }
        }

        APIService.api
            .get('apparel-categories', {
                params: {
                    return_only: 'id, name',
                    order_by: 'name',
                    order_direction: 'asc',
                    platform: getPlatformTag(),
                },
            })
            .then(({ data }) => setSports([...data.items, ...sports]));
    }, []);

    return (
        <div className={cx(classes.root, className)} ref={ref} {...rest}>
            <FormHeader />

            <div className={classes.content}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            name={'first_name'}
                            label={'First Name'}
                            placeholder={'First Name'}
                            value={values.first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classes.field}
                            error={errors.first_name && touched.first_name}
                            helperText={<ErrorMessage name={'first_name'} />}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            name={'last_name'}
                            label={'Last Name'}
                            placeholder={'Last Name'}
                            value={values.last_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classes.field}
                            error={errors.last_name && touched.last_name}
                            helperText={<ErrorMessage name={'last_name'} />}
                        />
                    </Grid>
                </Grid>

                <TextField
                    name={'email'}
                    label={'Email'}
                    placeholder={'Email'}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={classes.field}
                    error={errors.email && touched.email}
                    helperText={<ErrorMessage name={'email'} />}
                />

                <Grid style={{ padding: '4px 0 5px 0' }}>
                    <CountriesDropdown name={'country'} />
                </Grid>

                <Label>State</Label>
                <CountryStateDropdown name={'state'} country={values?.country?.name} />

                <TextField
                    name={'city_name'}
                    label={'City'}
                    placeholder={'City'}
                    value={values?.city_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={classes.field}
                    error={errors.city_name && touched.city_name}
                    helperText={<ErrorMessage name={'city_name'} />}
                />

                <PhoneField className={classes.field} specialLabel={'Phone Number'} />

                <Grid style={{ paddingTop: '4px' }}>
                    <Label>Sport</Label>
                    <TextField
                        select
                        name={'sport'}
                        value={values?.sport}
                        SelectProps={{ native: true }}
                        variant={'outlined'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classes.dropdownField}
                        error={errors.sport && touched.sport}
                        helperText={<ErrorMessage name={'sport'} />}
                    >
                        <option key={''} value={''}>
                            {'Select Sport'}
                        </option>

                        {sports?.length &&
                            sports.map((sport) => (
                                <option key={sport.id} value={sport.name}>
                                    {sport.name}
                                </option>
                            ))}
                    </TextField>

                    {values?.sport === 'Other' && (
                        <TextField
                            name={'sport_other'}
                            value={values?.sport_other}
                            placeholder={'Sport'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classes.field}
                            error={errors.sport_other && touched.sport_other}
                            helperText={<ErrorMessage name={'sport_other'} />}
                        />
                    )}
                </Grid>

                <TextField
                    name={'instagram_handle'}
                    label={'Instagram Handle'}
                    placeholder={'Instagram Handle'}
                    value={values?.instagram_handle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={classes.field}
                    error={errors.instagram_handle && touched.instagram_handle}
                    helperText={<ErrorMessage name={'instagram_handle'} />}
                />

                <Grid style={{ paddingTop: '4px' }}>
                    <Label>How did you find out about us</Label>
                    <TextField
                        select
                        name={'source'}
                        value={values?.source}
                        SelectProps={{ native: true }}
                        variant={'outlined'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classes.dropdownField}
                        error={errors.source && touched.source}
                        helperText={<ErrorMessage name={'source'} />}
                    >
                        <option key={''} value={''}>
                            {'Select Source'}
                        </option>

                        {sources.map((src) => (
                            <option key={src} value={src}>
                                {src}
                            </option>
                        ))}
                    </TextField>

                    {values?.source === 'Other' && (
                        <TextField
                            name={'source_other'}
                            value={values?.source_other}
                            placeholder={'How did you find out about us'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classes.field}
                            error={errors.source_other && touched.source_other}
                            helperText={<ErrorMessage name={'source_other'} />}
                        />
                    )}
                </Grid>

                <Grid style={{ paddingTop: '4px' }}>
                    <Label>What Role Best Describes You?</Label>
                    <TextField
                        select
                        name={'what_role'}
                        value={values?.what_role}
                        SelectProps={{ native: true }}
                        variant={'outlined'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classes.dropdownField}
                        error={errors.what_role && touched.what_role}
                        helperText={<ErrorMessage name={'what_role'} />}
                    >
                        <option key={''} value={''}>
                            {'Select Role'}
                        </option>

                        {whatRoles.map((role) => (
                            <option key={role} value={role}>
                                {role}
                            </option>
                        ))}
                    </TextField>

                    {values?.what_role === 'Other' && (
                        <TextField
                            name={'what_role_other'}
                            value={values?.what_role_other}
                            placeholder={'What Role Best Describes You?'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classes.field}
                            error={errors.what_role_other && touched.what_role_other}
                            helperText={<ErrorMessage name={'what_role_other'} />}
                        />
                    )}
                </Grid>

                <Grid style={{ paddingTop: '4px' }}>
                    <Label>
                        Are you over the age of 18 and authorized to place/pay for orders on behalf of your
                        organization?
                    </Label>
                    <FormControl
                        error={!!(errors.authorized_to_order && touched.authorized_to_order)}
                        component={'fieldset'}
                    >
                        <RadioGroup
                            name={'authorized_to_order'}
                            value={
                                values.authorized_to_order === true
                                    ? 'true'
                                    : values.authorized_to_order === false
                                    ? 'false'
                                    : ''
                            }
                            onChange={(event) => setFieldValue('authorized_to_order', event.target.value === 'true')}
                            row
                        >
                            <FormControlLabel value={'true'} control={<Radio />} label={'Yes'} />
                            <FormControlLabel value={'false'} control={<Radio />} label={'No'} />
                        </RadioGroup>
                        {errors.authorized_to_order && touched.authorized_to_order && (
                            <FormHelperText>
                                <ErrorMessage name={'authorized_to_order'} />
                            </FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <TextField
                    type={showPassword ? 'text' : 'password'}
                    name={'password'}
                    label={'Create Password'}
                    placeholder={'Password'}
                    value={values.password}
                    onChange={handleChange}
                    className={classes.field}
                    error={errors.password && touched.password}
                    helperText={<ErrorMessage name={'password'} />}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Toggle password visibility"
                                    onClick={handleToggleShowPassword}
                                    onMouseDown={handleToggleShowPassword}
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Ghost attribution fields — populated from URL/localStorage, not shown to users */}
                <input type="hidden" name="utm_source" value={(values as any).utm_source || ''} readOnly />
                <input type="hidden" name="utm_medium" value={(values as any).utm_medium || ''} readOnly />
                <input type="hidden" name="utm_campaign" value={(values as any).utm_campaign || ''} readOnly />
                <input type="hidden" name="gclid" value={(values as any).gclid || ''} readOnly />
                <input type="hidden" name="fbclid" value={(values as any).fbclid || ''} readOnly />
                <input type="hidden" name="ttclid" value={(values as any).ttclid || ''} readOnly />
                <input type="hidden" name="msclkid" value={(values as any).msclkid || ''} readOnly />

                <div className={classes.actions}>
                    <Button
                        type={'submit'}
                        variant={'contained'}
                        color={'primary'}
                        className={classes.button}
                        disableElevation
                    >
                        {dialog$.cta ?? 'Create Account'}
                    </Button>
                </div>

                <Divider className={classes.divider} />
            </div>

            <div className={cx(classes.footer, 'signup')}>
                <Button
                    type={'button'}
                    className={classes.button}
                    variant={'contained'}
                    onClick={handleView(targetSignInView)}
                >
                    Already have an account?
                </Button>
            </div>
        </div>
    );
});

export const Label = styled.div`
    color: rgba(0, 0, 0, 0.87);
    font-size: 12px;
    margin-top: 8px;
    margin-bottom: -2px;
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: flex-start;
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.1px;
`;

export default SignUp;
